import { useEffect, useRef, useState } from "react"

import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from "@/domains/icons/huge-icons/monotone"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

import type { RevenueSourceAllocationCardViewProps } from "./types"

export const RevenueSourceAllocationCardView = ({
  revenueSource,
  onRemove,
  onUpdateAllocation,
}: RevenueSourceAllocationCardViewProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setIsEditing(false)
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      onUpdateAllocation(revenueSource.id, Math.min(100, revenueSource.allocation + 1))
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      onUpdateAllocation(revenueSource.id, Math.max(0, revenueSource.allocation - 1))
    }
  }

  return (
    <div className="group relative flex min-h-[80px] items-stretch overflow-hidden rounded-xl border border-p400-light bg-p500 p-0 transition-all duration-200 hover:border-primary-400/30 hover:bg-p400">
      {/* Remove button */}
      <button
        onClick={() => onRemove(revenueSource.id)}
        className="flex w-10 items-center justify-center border-r border-p400-light text-t300 transition-colors hover:bg-red-500/10 hover:text-red-400"
      >
        <CloseIcon size={16} />
      </button>

      {/* Main content */}
      <div className="flex flex-1 flex-col justify-center p-3">
        <div className="mb-1 flex items-center gap-1.5">
          <DisplayTokens tokens={revenueSource.tokens} tokenSize={20} />
          <span className="text-sm font-bold tracking-tight text-t100">
            {revenueSource.displayName ?? "Locating source..."}
          </span>
        </div>
        <div className="truncate text-[10px] font-medium text-t300 sm:text-xs">
          ${revenueSource.perVote.toFixed(6)} / vote •{" "}
          <span className="text-green-400">${revenueSource.expectedIncome.toFixed(2)} est.</span>
        </div>
      </div>

      {/* Percentage input section */}
      <div className="group/input relative flex w-20 flex-col items-center justify-center border-l border-p400-light bg-p500 sm:w-24">
        {isEditing ? (
          <div className="flex h-full w-full items-center justify-center">
            <input
              ref={inputRef}
              type="number"
              value={revenueSource.allocation}
              onChange={(e) => onUpdateAllocation(revenueSource.id, Number(e.target.value))}
              onBlur={() => setIsEditing(false)}
              onKeyDown={handleKeyDown}
              className="h-full w-full bg-transparent text-center text-xl font-bold text-primary-400 outline-none [-moz-appearance:textfield] focus:bg-primary-900/20 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              min="0"
              max="100"
            />
          </div>
        ) : (
          <div
            onClick={() => setIsEditing(true)}
            className="flex h-full w-full cursor-pointer items-center justify-center transition-colors hover:bg-primary-500/10"
          >
            <span className="text-xl font-bold text-t100">{revenueSource.allocation}%</span>
          </div>
        )}

        {/* Up/Down buttons (visible on hover) */}
        <div className="absolute bottom-0 right-0 top-0 flex flex-col opacity-0 transition-opacity group-hover/input:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onUpdateAllocation(revenueSource.id, Math.min(100, revenueSource.allocation + 1))
            }}
            className="flex h-1/2 items-center justify-center px-1 text-t300 transition-colors hover:bg-p400 hover:text-t100"
          >
            <ChevronUpIcon size={12} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onUpdateAllocation(revenueSource.id, Math.max(0, revenueSource.allocation - 1))
            }}
            className="flex h-1/2 items-center justify-center px-1 text-t300 transition-colors hover:bg-p400 hover:text-t100"
          >
            <ChevronDownIcon size={12} />
          </button>
        </div>
      </div>
    </div>
  )
}
